import {Box, BoxProps, Tab, Tabs, TabsProps, useTheme} from '@mui/material';
import React, {useEffect, useState} from 'react';

export interface AppTabSectionNode {
    sectionHeaderTitle: string;
    sectionRenderNode: React.ReactNode;
    key: string;
}

export interface AppTabSectionScrollProps extends TabsProps {
    sections: AppTabSectionNode[];
    tabBodySectionProps?: BoxProps;
}

export const AppTabSectionScroll: React.FC<AppTabSectionScrollProps> = ({sections, tabBodySectionProps, ...prop}) => {
    const theme = useTheme();
    const [value, setValue] = useState<number>(0);

    // const isMywork = useLocation().pathname.includes('mywork');

    const scrollOnSectionClickHandler = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    };

    const handleChange = (event: React.SyntheticEvent | null, newValue: number) => {
        setValue(newValue);
        scrollOnSectionClickHandler(sections[newValue].key);
    };

    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            const element = document.getElementById(hash.replace('#', ''));
            if (element) {
                element.scrollIntoView({behavior: 'smooth'});
            }
        }
    }, []);

    useEffect(() => {
        const container = document.getElementById('sectionsContainer');
        if (!container) return;

        const observerOptions = {
            root: container,
            rootMargin: '0px 0px -50% 0px',
            threshold: 0,
        };

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const index = sections.findIndex(section => section.sectionHeaderTitle === entry.target.id);
                    if (index !== -1) {
                        setValue(index);
                    }
                }
            });
        }, observerOptions);

        sections.forEach(section => {
            const el = document.getElementById(section.sectionHeaderTitle);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [sections]);

    return (
        <>
            <Tabs
                style={{
                    backgroundColor: theme.palette.secondary[200],
                    border: `${theme.palette.secondary.light}`,
                    borderBottom: `1px solid ${theme.palette.secondary.light}`,
                    borderRadius: '10px 10px 0 0',
                }}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                aria-label="scrollable force tabs example"
                value={value}
                onChange={handleChange}
                {...prop}
            >
                {sections.map((section: AppTabSectionNode, index: number) => (
                    <Tab key={`${section.sectionHeaderTitle}${index}`} label={section.sectionHeaderTitle} />
                ))}
            </Tabs>
            <Box
                bgcolor="white"
                id="sectionsContainer"
                p={1}
                sx={{overflowY: 'auto', maxHeight: '80vh'}}
                {...tabBodySectionProps}
            >
                {sections.map((section: AppTabSectionNode, index: number) => (
                    <Box id={section.key} key={section.key}>
                        {section.sectionRenderNode}
                    </Box>
                ))}
            </Box>
        </>
    );
};

export default AppTabSectionScroll;
